<template>
  <q-page class="main-wrapper-page single-page" style="background-color: var(--q-accent)">
    <div v-if="workflow" class="row no-wrap q-col-gutter-md">
      <div class="col col-xl-2 col-lg-3 col-md-4">
        <q-scroll-area
          style="width: 100%; padding-right: 10px"
          :style="`height: calc(100vh - ${currentBanner ? '200px' : '150px'})`"
          :thumb-style="{ borderRadius: '5px', background: '#9f9f9f', width: '3px', opacity: 1, right: 0 }"
        >
          <h3 class="q-mb-sm q-mt-none">Info</h3>
          <div class="info-holder">
            <ul>
              <li><b>Name:</b> {{ workflow.workflowId }}</li>
              <li>
                <b>UUID:</b>
                <span style="word-break: break-all"
                  >{{ workflow.uuid }}
                  <q-icon
                    name="engp:engp-ico-copy"
                    @click.stop="copyUuid(workflow.uuid)"
                    size="14px"
                    class="q-ml-xs cursor-pointer"
                  >
                    <q-tooltip>Copy UUID</q-tooltip>
                  </q-icon>
                </span>
              </li>
              <li><b>Actor:</b>{{ workflow?.actor }}</li>
              <li class="flex">
                <b>Status:</b>
                <Status :workflow="workflow" />
              </li>
              <li><b>Created at:</b> {{ $options.dayjs(workflow.createdAt).format('DD.MM.YYYY HH:mm') }}</li>
              <li v-if="workflow.deletedAt">
                <b>Deleted at:</b> {{ $options.dayjs(workflow.deletedAt).format('DD.MM.YYYY HH:mm') }}
              </li>
              <li>
                <b>Process Instance ID: </b>
                <span class="instance-key cursor-pointer" @click="goToSco(workflow.processInstanceKey)">
                  {{ workflow.processInstanceKey }}</span
                >
              </li>
              <li>
                <b>Related workflows:</b>
                <span class="instance-key cursor-pointer" @click="goToRelatedWorkflows">Show all</span>
              </li>
            </ul>
          </div>
          <h3 class="q-mt-none q-mb-none" v-if="Object.keys(workflow.inputVariables).length">Input Variables</h3>
          <div v-if="Object.keys(workflow.inputVariables).length" class="info-holder">
            <ul>
              <li v-for="key in Object.keys(workflow.inputVariables)" :key="key">
                <b
                  >{{ key }}:
                  <q-tooltip v-if="key.length > 15">{{ key }}</q-tooltip>
                </b>
                <div style="word-break: break-all">
                  <span v-if="key === 'digitalServiceApmId'">
                    <router-link
                      :to="`/my-portfolio/${workflow.inputVariables[key]}`"
                      target="_blank"
                      class="instance-key"
                    >
                      {{ workflow.inputVariables[key] }}
                    </router-link>
                  </span>
                  <span v-else-if="typeof workflow.inputVariables[key] === 'string'">
                    {{ workflow.inputVariables[key] }}
                  </span>
                  <span v-else-if="typeof workflow.inputVariables[key] === 'boolean'">
                    {{ workflow.inputVariables[key] ? 'true' : 'false' }}
                  </span>
                  <span v-else-if="typeof workflow.inputVariables[key] === 'number'">
                    {{ workflow.inputVariables[key] }}
                  </span>
                  <span v-else @click="showJson(workflow.inputVariables[key], key)" class="cursor-pointer instance-key"
                    >Show</span
                  >
                </div>
              </li>
            </ul>
          </div>
        </q-scroll-area>
      </div>
      <div class="col col-xl-10 col-lg-9 col-md-8">
        <div class="q-px-md">
          <div class="flex items-center justify-between q-mb-sm">
            <h3 class="q-mt-none q-mb-none">Workflow Events</h3>
            <q-checkbox
              class="q-ml-auto"
              v-model="showRelations"
              @update:model-value="() => $nextTick(() => updateUrl())"
              label="Show related items"
            ></q-checkbox>
            <span class="divider"></span>
            <q-btn
              flat
              no-caps
              class="q-ml-md btn--no-hover text-weight-regular"
              size="15px"
              style="min-height: 30px; padding: 0"
              :ripple="false"
              @click="sortByDate"
            >
              <q-icon
                size="18px"
                class="q-mr-sm"
                :name="!sortByAsc ? 'engp:engp-ico-time_down' : 'engp:engp-ico-time_up'"
              ></q-icon>
              <span>{{ !sortByAsc ? 'New to old' : 'Old to new' }}</span>
            </q-btn>
            <span class="divider"></span>
            <q-btn
              flat
              no-caps
              class="q-ml-xs btn--no-hover text-weight-regular"
              round
              :icon="!expandAll ? 'sym_o_unfold_more' : 'sym_o_unfold_less'"
              size="15px"
              style="min-height: 30px"
              :ripple="false"
              @click="expandCollapseAll"
            >
              <q-tooltip>{{ expandAll ? 'Collapse all' : 'Expand all' }}</q-tooltip>
            </q-btn>
          </div>
          <q-scroll-area
            style="width: 100%; padding-right: 10px"
            :style="`height: calc(100vh - ${currentBanner ? '250px' : '200px'})`"
            :thumb-style="{ borderRadius: '5px', background: '#9f9f9f', width: '3px', opacity: 1, right: 0 }"
          >
            <div v-if="!sortedEvents.length">
              <div class="no-events flex column items-center">
                <q-icon name="engp:engp-ico-empty" size="60px" class="q-mb-md" color="primary"></q-icon>
                <p>Workflow hasn't produced any events yet.</p>
              </div>
            </div>
            <event
              v-for="event in sortedEvents"
              :key="event.uuid"
              class="q-mb-md"
              @reload="load"
              :eventData="event"
              :workflowProcessInstanceKey="workflow.processInstanceKey"
            ></event>
          </q-scroll-area>
        </div>
      </div>
    </div>
    <q-dialog v-model="showJsonDialog" @hide="closeJsonDialog">
      <q-card style="width: 520px; max-width: 80vw">
        <q-toolbar class="shadow-2 q-py-sm q-px-md">
          <q-toolbar-title class="text-weight-bold">
            <strong>{{ jsonDialogKey }}</strong>
          </q-toolbar-title>
          <q-btn icon="close" style="min-width: 20px" round flat class="btn--no-hover" v-close-popup></q-btn>
        </q-toolbar>

        <q-card-section class="q-pt-none service-description-markdown full-width">
          <div class="json-viewer" v-html="renderedJson"></div>
        </q-card-section>
      </q-card>
    </q-dialog>
  </q-page>
</template>

<script>
import dayjs from 'dayjs';
import { marked } from 'marked';
import { copyToClipboard } from 'quasar';
import { mapGetters } from 'vuex';
import emitter from 'tiny-emitter/instance';
import { BANNERS_GETTERS } from '@/store/modules/banners';
import { getWorkflowById, getRelationsByWorkflow, getRelationsByItem } from './api';
import Event from './components/Event.vue';
import Status from './components/Status.vue';

export default {
  name: 'WorkFlow',
  components: {
    Event,
    Status,
  },
  emits: ['reload'],
  copyToClipboard,
  dayjs,
  data() {
    return {
      sortByAsc: false,
      expandAll: false,
      workflow: undefined,
      events: [],
      showRelations: true,
      showJsonDialog: false,
      jsonContent: '',
      jsonDialogKey: '',
    };
  },
  computed: {
    ...mapGetters('banners', {
      currentBanner: BANNERS_GETTERS.BANNERS_SHOW,
    }),
    sortedEvents() {
      const filtered = this.showRelations ? this.events : this.events.filter((event) => event.type !== 'relation');

      return this.sortEvents(filtered, this.sortByAsc);
    },
    renderedJson() {
      return this.jsonContent ? marked(this.jsonContent) : '';
    },
  },
  methods: {
    showJson(data, key) {
      const formattedJson = '```json\n' + JSON.stringify(data, null, 2) + '\n```';
      this.jsonContent = formattedJson;
      this.jsonDialogKey = key;
      this.showJsonDialog = true;
    },
    closeJsonDialog() {
      this.jsonContent = '';
      this.jsonDialogKey = '';
    },
    goToRelatedWorkflows() {
      this.$router.push(`/workflows?itemId=${this.workflow.uuid}`);
    },
    sortByDate() {
      this.sortByAsc = !this.sortByAsc;
      this.updateUrl();
    },
    updateUrl() {
      const query = {};
      this.showRelations ? (query.showRelations = true) : (query.showRelations = false);
      this.sortByAsc ? (query.sort = 'oldToNew') : (query.sort = 'newToOld');
      this.$router.replace({ query: query });
    },
    sortEvents(events, sort) {
      return events
        .sort((a, b) => {
          const dateA = new Date(a.createdAt);
          const dateB = new Date(b.createdAt);

          if (sort) {
            return dateA - dateB;
          } else {
            return dateB - dateA;
          }
        })
        .map((event) => {
          if (event.events && Array.isArray(event.events)) {
            event.events = this.sortEvents(event.events, sort);
          }
          return event;
        });
    },
    expandCollapseAll() {
      this.expandAll = !this.expandAll;
      emitter.emit('expandCollapseAll', this.expandAll);
    },
    buildEventsTree(events) {
      const map = {};
      const roots = [];
      const mapStatus = {
        resolved: 3,
        pending: 2,
        cancelled: 1,
      };

      events.forEach((event) => {
        if (event?.subscriptions.length) {
          event.subscriptions.sort((a, b) => mapStatus[b.status] - mapStatus[a.status]);
        }
        map[event.uuid] = { ...event, events: [], hasChildError: false };
      });

      events.forEach((event) => {
        if (event.eventUuid) {
          if (map[event.eventUuid]) {
            map[event.eventUuid].events.push(map[event.uuid]);
          }
        } else {
          roots.push(map[event.uuid]);
        }
      });

      const setHasChildError = (node) => {
        if (node.events.length > 0) {
          node.events.forEach((child) => setHasChildError(child));
          node.hasChildError = node.events.some((child) => child.isError || child.hasChildError);
        }
      };

      roots.forEach((root) => setHasChildError(root));

      return roots;
    },
    copyUuid(uuid) {
      copyToClipboard(uuid).then(() => {
        this.$notify('info', 'Info', 'Workflow UUID was copied');
      });
    },
    goToSco(id) {
      window.open(`${process.env.VUE_APP_OASIS_URL}/${id}`, '_blank');
    },
    async load() {
      this.$showLoading();
      try {
        const [workflow, relatedWorkflows, relatedItems] = await Promise.all([
          getWorkflowById(this.$route.params.id),
          getRelationsByWorkflow(this.$route.params.id),
          getRelationsByItem(this.$route.params.id),
        ]);

        this.workflow = workflow;
        const processedRelatedItems = relatedItems.map((item) => ({
          ...item,
          type: 'relation',
          subscriptions: [],
          isRelatedItem: true,
        }));

        const processedRelatedWorkflows = relatedWorkflows.map((item) => ({
          ...item,
          type: 'relation',
          subscriptions: [],
        }));

        this.events = this.buildEventsTree([
          ...this.workflow.events,
          ...processedRelatedWorkflows,
          ...processedRelatedItems,
        ]);
      } catch (error) {
        this.$notify('negative', 'Error', error.message);
      } finally {
        this.$hideLoading();
      }
    },
  },
  async mounted() {
    const query = this.$route.query;
    if (query.showRelations === 'false') {
      this.showRelations = false;
    }
    if (query.sort === 'oldToNew') {
      this.sortByAsc = true;
    } else {
      this.sortByAsc = false;
    }
    await this.load();
  },
};
</script>

<style lang="scss" scoped>
h3 {
  font-size: 1.7em;
}
.info-holder {
  background-color: #e7e9f1;
  padding: 15px;
  border-radius: 15px;
  margin-bottom: 10px;
  ul {
    margin: 0;
    padding: 0;
    list-style-type: none;
    li {
      display: flex;
      align-items: flex-start;
      padding: 10px 0;
      border-bottom: 1px solid #d4d8e8;

      &:last-child {
        border-bottom: none;
      }

      b {
        display: block;
        text-transform: capitalize;
        min-width: 135px;
        width: 135px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        padding-right: 5px;
      }
    }
  }
  .instance-key {
    color: $blue;
  }
}
.no-events {
  background-color: #fff;
  padding: 92px;
  text-align: center;
  border-radius: 15px;
  font-size: 16px;
}
.divider {
  width: 1px;
  height: 24px;
  display: block;
  background-color: #b3b5bb;
  margin-left: 20px;
}
.service-description-markdown.full-width {
  max-width: 100%;
}
</style>
